/**
 * sessionState.js — что было открыто, чтобы вернуться туда после выгрузки вкладки.
 *
 * Зачем. Отчёт с устройства дважды показал «старт-после-выгрузки»: мобильный
 * браузер убивает фоновую вкладку целиком. Это не случаи A/B/C — там программа
 * жива и чинит себя сама. Здесь её в момент смерти просто нет: ни обработчика,
 * ни памяти, ни тома. Изнутри чинить нечего, и в 7.0.59 я честно написал, что
 * лечится это только сохранением состояния сеанса. Вот оно.
 *
 * Что можно и чего нельзя — важное различие.
 *
 *   Исследование лежит в базе (его открывали из архива или сохраняли туда) —
 *   тогда возвращается всё: файлы читаются заново, вид восстанавливается.
 *
 *   Файлы открывали прямо с диска и в базу не клали — тогда пиксели вернуть
 *   НЕЛЬЗЯ ничем. Браузер не хранит выбранные файлы между запусками, и обещать
 *   тут нечего. Но настройки вида — раскладка, окно, положение среза — мы
 *   помним и применим, как только ту же серию откроют снова. Это меньше, чем
 *   хотелось бы, и говорить об этом надо прямо, а не делать вид, что всё цело.
 *
 * Модуль без браузерных зависимостей, кроме localStorage: правило «что можно
 * предложить» — чистое и проверяется целиком.
 */

const KEY = 'mikhmel.session.v1';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;   // сутки спустя предлагать уже неуместно

let _mem = null;   // копия в памяти: пишем часто, читаем редко

function _read() {
  if (_mem) return _mem;
  try {
    const raw = localStorage.getItem(KEY);
    _mem = raw ? JSON.parse(raw) : null;
  } catch (e) { _mem = null; }
  return _mem;
}

function _write(obj) {
  _mem = obj;
  try { localStorage.setItem(KEY, JSON.stringify(obj)); } catch (e) { /* переполнение — не беда */ }
}

/** Запомнить, что открыли исследование из базы (или положили его туда). */
export function noteStudyOpened({ studyId, label, patientName } = {}) {
  if (!studyId) return null;
  const s = _read() || {};
  const next = {
    ...s,
    studyId,
    label: label || patientName || s.label || '',
    at: Date.now(),
  };
  _write(next);
  return next;
}

/** Запомнить показанный том и вьюпорты. */
export function noteVolumeShown({ volumeId, viewportIds, viewMode, layout } = {}) {
  const s = _read() || {};
  const next = {
    ...s,
    volumeId: volumeId || s.volumeId || null,
    viewportIds: Array.isArray(viewportIds) && viewportIds.length ? viewportIds.slice() : (s.viewportIds || []),
    viewMode: viewMode || s.viewMode || null,
    layout: layout || s.layout || null,
    at: Date.now(),
  };
  _write(next);
  return next;
}

/** Запомнить вид: камера, окно, срез. Кладём как есть, разбирать не наше дело. */
export function noteViewState(view) {
  if (!view) return null;
  const s = _read() || {};
  const next = { ...s, view, at: Date.now() };
  _write(next);
  return next;
}

export function getSession() { return _read(); }

export function clearSession() {
  _mem = null;
  try { localStorage.removeItem(KEY); } catch (e) { /* */ }
  return true;
}

/**
 * Что мы можем предложить человеку при старте.
 *
 * Правило вынесено отдельно и проверяется целиком, потому что ошибиться тут
 * дорого в обе стороны: пообещать возврат и не смочь — хуже, чем не обещать;
 * промолчать, когда вернуть можно, — значит зря потерять работу.
 *
 * @param {object} a
 *   session       — что сохранено (или null)
 *   wasDiscarded  — вкладку выгрузил браузер (метка ухода в фон осталась непогашенной)
 *   studyInDb     — исследование лежит в базе и файлы можно прочитать заново
 *   now           — текущее время в мс (передаётся, чтобы правило было проверяемым)
 * @returns {{action:'none'|'reopen'|'view-only', title:string, detail:string,
 *            studyId:string|null, canRestoreView:boolean}}
 */
export function planSessionRestore(a = {}) {
  const s = a.session;
  const now = Number.isFinite(a.now) ? a.now : 0;
  const none = { action: 'none', title: '', detail: '', studyId: null, canRestoreView: false };

  if (!s || !s.at) return none;
  if (!a.wasDiscarded) return none;                     // обычный запуск — не лезем
  if (now && now - s.at > MAX_AGE_MS) return none;      // слишком давно, предложение неуместно

  const what = s.label ? `«${s.label}»` : 'последнее исследование';

  if (s.studyId && a.studyInDb) {
    return {
      action: 'reopen',
      title: 'Вкладку выгрузил браузер',
      detail: `Открыть ${what} заново и вернуть вид?`,
      studyId: s.studyId,
      canRestoreView: !!s.view,
    };
  }

  if (s.view || s.volumeId) {
    return {
      action: 'view-only',
      title: 'Вкладку выгрузил браузер',
      detail: 'Снимки открывались с диска — браузер их не хранит, вернуть их сам я не могу. '
            + 'Откройте ту же папку, и раскладку, окно и срез я восстановлю.',
      studyId: null,
      canRestoreView: !!s.view,
    };
  }

  return none;
}

export default {
  noteStudyOpened, noteVolumeShown, noteViewState,
  getSession, clearSession, planSessionRestore,
};
